/**
 * Office document repair — UI layer.
 *
 * Every file first gets its ANATOMY (file-anatomy.js) and a route (route.js): what its
 * bytes are decides the answer, not its name. Census of 16.10.2026: most files that
 * «could not be repaired» were not broken packages at all — an old .doc under a .docx
 * name, a password-encrypted document, a file of zeros, Word's «~$» owner file, an
 * HTML page saved as .docx — and each got the same dead end. Now each gets its own:
 *
 *   package    REPAIRED / PARTIAL (what was lost is named) / TEXT ONLY — the package
 *              repair, measured to give back files Office opens (docs/why/office-repair.md);
 *   binary     an old Word/Excel/PowerPoint file: its text here, in the browser; the
 *              document itself rebuilt by LibreOffice on our server — Premium, through the
 *              office lane's door, which says why to everyone else;
 *   renamed    the same bytes under the right extension (Word refuses the name, not the file);
 *   encrypted / zeros / lock / foreign / headless — said as they are.
 */

import { examine, repair, extractText, mediaParts } from './ooxml-repair.js';
import { fileAnatomy, reportAnatomy } from './file-anatomy.js';
import { routeFile, renamedAs, extOf } from './route.js';
import { foreignText } from './foreign-text.js';
import { recoverBinaryText } from './binary-text.js';

const T = (k, p) => {
  let s = (window.__OR_I18N && window.__OR_I18N[k]) || k;
  if (p) for (const [a, b] of Object.entries(p)) s = s.replace(new RegExp(':' + a, 'g'), b);
  return s;
};
const el = (id) => document.getElementById(id);
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function human(bytes) {
  const u = [T('unit_b'), T('unit_kb'), T('unit_mb'), T('unit_gb')];
  let n = Number(bytes) || 0, i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n < 10 && i ? n.toFixed(1) : Math.round(n)} ${u[i]}`;
}

let current = null;
let door = null;

/* The office lane (public/js/office-lane.js, window.OfficeLane): imported relative to this
 * module, so it rides the same versioned path. Null when the lane is off here. */
async function officeLane() {
  try { if (!window.OfficeLane) await import('../office-lane.js'); } catch (_) { /* lane script unavailable */ }
  const OL = window.OfficeLane;
  return OL && OL.available() ? OL : null;
}

function card(tone, title, body, extra) {
  return `<div class="or-card or-tone-${tone}"><p class="or-title">${title}</p>${body ? `<p class="or-body">${body}</p>` : ''}${extra || ''}</div>`;
}
function saveButton(id, label) {
  return `<div class="or-actions"><button type="button" class="or-btn" id="${id}">${esc(label)}</button></div>`;
}
function finish(html, outcome, extra) {
  const out = el('or-out');
  out.innerHTML = html;
  out.hidden = false;
  wire();
  // one row per file: what the bytes were and which answer they got (feature_use only)
  reportAnatomy(current.file, outcome, extra);
}

async function analyse(file) {
  if (door) { try { door.destroy(); } catch (_) { /* gone */ } door = null; }
  const out = el('or-out');
  out.innerHTML = `<div class="or-working">${T('working')}</div>`;
  out.hidden = false;
  window.__stage && window.__stage('analyzing');

  let a = null;
  try { a = await fileAnatomy(file); } catch (_) { a = null; }
  const r = routeFile(a, file.name);
  current = { file, anatomy: a, route: r };

  switch (r.route) {
    case 'zeros':
      return finish(card('bad', T('class_zeros_title'), T('class_zeros_body', { size: human(file.size) })), 'zeros');
    case 'lock':
      return finish(card('warn', T('class_lock_title'), T('class_lock_body', { name: esc(String(file.name || '').replace(/^~\$/, '')) })), 'lock');
    case 'encrypted':
      return finish(card('warn', T('class_encrypted_title'), T('class_encrypted_body')), 'encrypted');
    case 'foreign': return answerForeign(file, r);
    case 'binary': return answerBinary(file, a, r);
    case 'headless': return answerHeadless(file, a, r);
    case 'package': return answerPackage(file, r);
    default:
      return finish(card('bad', T('class_unknown_title'), T('class_unknown_body')), 'unknown');
  }
}

/* ── another format wearing an office name ─────────────────────────────── */
async function answerForeign(file, r) {
  let text = '';
  if (['rtf', 'html', 'mht', 'xml', 'text'].includes(r.format) && file.size < (64 << 20)) {
    try { text = foreignText(new Uint8Array(await file.arrayBuffer()), r.format); } catch (_) { text = ''; }
  }
  current.text = text;
  current.media = [];
  const salvage = renderSalvage(text, []);
  const fmt = T('fmt_' + (r.as === 'csv' ? 'csv' : r.format));
  // Already under its own extension (an .rtf that is RTF): nothing to rename — the text is the help.
  if (extOf(file.name) === r.as) {
    return finish(card('warn', T('class_foreign_native_title', { format: fmt }), T('class_foreign_native_body')) + salvage, 'foreign_native', { as: r.as });
  }
  const name = renamedAs(file.name, r.as);
  // Text in a legacy code page, and any table, goes back as UTF-8 with a BOM — the one form
  // that opened right in Excel and Notepad on every system measured (route.js, 23.10.2026);
  // anything else is the same bytes under the right extension.
  const reencode = r.format === 'text' && text && (r.enc || r.as === 'csv');
  const blob = reencode
    ? new Blob(['\uFEFF' + (r.as === 'csv' ? text.replace(/\n/g, '\r\n') : text)], { type: (r.as === 'csv' ? 'text/csv' : 'text/plain') + ';charset=utf-8' })
    : file;
  current.renamed = { blob, name };
  finish(card('warn', T('class_foreign_title', { format: fmt }),
    T('class_foreign_body', { ext: '.' + r.as }), saveButton('or-save-renamed', T('save_renamed', { name }))) + salvage,
  'foreign', { as: r.as, reenc: reencode ? 1 : 0 });
}

/* ── an old binary Word / Excel / PowerPoint file ──────────────────────── */
async function answerBinary(file, a, r) {
  const OL = await officeLane();
  if (r.filePass && OL) {
    // An Excel FilePass is a password — or Excel's default one for a protected structure.
    try { const c = await OL.classify(file); if (c && c.encrypted) return finish(card('warn', T('class_encrypted_title'), T('class_encrypted_body')), 'encrypted'); } catch (_) { /* decide below */ }
  }
  const kind = T('kind_' + (r.family || 'word'));
  const t = recoverBinaryText(new Uint8Array(await file.arrayBuffer()));
  current.text = t.text;
  current.media = [];
  const salvage = renderSalvage(t.text, []);

  if (r.renamed) {
    const name = renamedAs(file.name, r.renamed);
    current.renamed = { blob: file, name };
    return finish(card('ok', T('class_renamed_title', { kind, ext: '.' + (String(file.name).split('.').pop() || '') }),
      T('class_renamed_body', { right: '.' + r.renamed }), saveButton('or-save-renamed', T('save_renamed', { name }))) + salvage,
    'binary_renamed', { words: t.words });
  }

  let html;
  if (t.sound) html = card('warn', T('class_legacy_sound_title', { kind }), T('class_legacy_sound_body'));
  else if (t.words) html = card('warn', T('class_legacy_damaged_title', { kind }), T('class_legacy_damaged_body', { n: String(t.words) }));
  else html = card('bad', T('class_legacy_nothing_title', { kind }), T('class_legacy_nothing_body'));

  // The document itself: only a full office suite rebuilds it (LibreOffice, our server,
  // Premium). This route means the compound-file signature is intact — the server takes
  // such a file even with its directory lost (measured: a truncated .xls came back as a
  // book Excel opened 36/36); a file whose start is zeroed never comes here (headless).
  html += '<div id="or-door"></div>';
  finish(html + salvage, t.sound ? 'binary_sound' : (t.words ? 'binary_text' : 'binary_nothing'), { words: t.words, how: t.how, cfb_ok: a && a.cfb_ok ? 1 : 0 });

  if (OL) {
    const host = el('or-door');
    if (host) door = OL.door(host, { file, tool: 'office-repair', mode: 'repair', info: { status: 'legacy', family: r.family || 'word', format: r.format || 'doc', encrypted: false } });
  }
}

/* ── the start of the file is gone ────────────────────────────────────── */
function answerHeadless(file, a, r) {
  return file.arrayBuffer().then((buf) => {
    const t = recoverBinaryText(new Uint8Array(buf));
    current.text = t.text;
    current.media = [];
    if (!t.words) return finish(card('bad', T('class_unknown_title'), T('class_unknown_body')), 'headless_nothing');
    finish(card('warn', T('class_headless_title'), T('class_headless_body')) + renderSalvage(t.text, []), 'headless_text', { words: t.words });
  });
}

/* ── a zip of parts: the package repair ───────────────────────────────── */
async function answerPackage(file, r) {
  let info;
  try {
    info = await examine(file, {});
  } catch (err) {
    const code = (err && err.code) || 'generic';
    window.__ae && window.__ae('tool_blocked', { slug: 'office-repair', reason: code, size: file.size });
    return finish(card('bad', T('err_' + code)), code);
  }

  /* The run is spent here, not on upload: examine() alone tells the user whether
   * their document is even damaged, and billing a day's run for the answer "it
   * was fine all along" is the thing CLAUDE.md calls out by name. */
  const damaged = info.issues.some((i) => i.code !== 'part_skipped');
  const _run = window.__toolLimit && damaged
    ? window.__toolLimit.begin('office-repair',
        window.__toolLimit.fileKey ? window.__toolLimit.fileKey(file) : null)
    : null;
  if (window.__toolLimit && damaged && !_run) {
    // The wall is begin()'s to show; «Разбираем документ…» must not stay behind it forever.
    const out = el('or-out');
    out.innerHTML = '';
    out.hidden = true;
    reportAnatomy(current.file, 'limit', null, { anatomy: current.anatomy || undefined });
    return;
  }

  let result = null, failure = null;
  if (damaged || !info.kind) {
    try {
      result = await repair(file, { examined: info, name: file.name });
    } catch (err) {
      failure = (err && err.code) || 'generic';
    }
  }

  const text = extractText(info.recovered, info.kind && info.kind.id);
  const media = mediaParts(info.recovered);
  Object.assign(current, { info, result, text, media });

  // A healthy modern file under an old name: the same bytes, the right extension.
  let renamed = '';
  if (!damaged && info.kind && r.renamed) {
    const name = renamedAs(file.name, r.renamed);
    current.renamed = { blob: file, name };
    renamed = card('ok', T('class_renamed_title', { kind: r.renamed.toUpperCase(), ext: '.' + (String(file.name).split('.').pop() || '') }),
      T('class_renamed_body', { right: '.' + r.renamed }), saveButton('or-save-renamed', T('save_renamed', { name })));
  }

  const html = (renamed || renderVerdict(info, result, failure, damaged)) + [
    renderLost(result),
    renderIssues(info),
    renderParts(info),
    renderSalvage(text, media),
  ].join('');
  if (_run) { if (result) _run.commit(); else _run.abort(); }
  window.__aeToolComplete && window.__aeToolComplete({
    kind: (info.kind && info.kind.id) || 'unknown',
    damaged: damaged ? 1 : 0, repaired: result ? 1 : 0,
    issues: info.issues.length, parts: info.parts.length,
    dropped: result ? result.report.dropped.length : -1,
    text_chars: text.length, media: media.length,
  });
  const outcome = failure || (!info.kind ? 'no_kind' : (!damaged ? (r.renamed ? 'package_renamed' : 'healthy') : (result.report.dropped.length ? 'partial' : 'repaired')));
  finish(html, outcome, { dropped: result ? result.report.dropped.length : -1 });
}

function renderVerdict(info, result, failure, damaged) {
  if (!damaged && info.kind) {
    return `<div class="or-card or-tone-ok">
        <p class="or-title">${T('verdict_healthy')}</p>
        <p class="or-body">${T('verdict_body_healthy', { kind: info.kind.label })}</p></div>`;
  }
  if (!result) {
    return `<div class="or-card or-tone-bad">
        <p class="or-title">${T('verdict_failed')}</p>
        <p class="or-body">${T('err_' + (failure || 'generic'))}</p></div>`;
  }
  const dropped = result.report.dropped.length;
  const tone = dropped ? 'warn' : 'ok';
  const title = dropped ? T('verdict_partial') : T('verdict_repaired');
  const body = dropped
    ? T('verdict_body_partial', { n: String(dropped), parts: String(result.report.parts) })
    : T('verdict_body_repaired', { parts: String(result.report.parts) });

  const fixes = result.report.fixes.length
    ? `<ul class="or-list">${result.report.fixes.map((f) =>
        `<li><code>${esc(f.path)}</code> — ${f.fixes.map((x) => T('fix_' + x.replace(/\d+/, 'n')) || x).join(', ')}</li>`).join('')}</ul>`
    : '';

  return `<div class="or-card or-tone-${tone}">
      <p class="or-title">${title}</p>
      <p class="or-body">${body}</p>
      ${fixes}
      <div class="or-actions"><button type="button" class="or-btn" id="or-save">${T('save', { name: result.filename })}</button></div>
    </div>`;
}

/** What did NOT make it into the rebuilt file — named, never «nothing lost». */
function renderLost(result) {
  if (!result || !result.report.dropped.length) return '';
  const items = result.report.dropped.map((d) => `<li><code>${esc(d.path)}</code> — ${T('drop_' + d.reason)}</li>`);
  return `<section class="or-block"><h3>${T('lost_title')}</h3><ul class="or-list">${items.join('')}</ul></section>`;
}

function renderIssues(info) {
  const real = info.issues.filter((i) => i.code !== 'part_skipped');
  if (!real.length) return '';
  const items = real.map((i) =>
    `<li>${T('issue_' + i.code, { path: i.path || '', reason: i.reason || '' })}${i.path ? ` <code>${esc(i.path)}</code>` : ''}</li>`);
  return `<section class="or-block"><h3>${T('issues_title')}</h3><ul class="or-list">${items.join('')}</ul></section>`;
}

/** The package map — the thing no competitor shows. */
function renderParts(info) {
  if (!info.parts.length) return '';
  const rows = info.parts.map((p) => {
    const bad = p.status === 'damaged' || p.status === 'partial' || (p.xml && p.xml.error);
    return `<tr class="${bad ? 'or-row-bad' : ''}">
        <td><code>${esc(p.path)}</code></td>
        <td>${human(p.bytes)}</td>
        <td>${T('status_' + p.status)}${p.xml && p.xml.error ? ' · ' + T('status_xml_bad') : ''}</td>
      </tr>`;
  }).join('');
  return `<section class="or-block"><h3>${T('parts_title', { n: String(info.parts.length) })}</h3>
      <div class="or-scroll"><table class="or-table">${rows}</table></div></section>`;
}

function renderSalvage(text, media) {
  if (!text && !media.length) return '';
  const preview = text
    ? `<pre class="or-text">${esc(text.slice(0, 4000))}${text.length > 4000 ? '\n…' : ''}</pre>
       <div class="or-actions"><button type="button" class="or-btn or-btn-ghost" id="or-save-text">${T('save_text')}</button></div>`
    : `<p class="or-body">${T('no_text')}</p>`;
  const mediaLine = media.length
    ? `<div class="or-actions"><button type="button" class="or-btn or-btn-ghost" id="or-save-media">${T('save_media', { n: String(media.length) })}</button></div>`
    : '';
  return `<section class="or-block"><h3>${T('salvage_title')}</h3>
      <p class="or-body">${T('salvage_desc')}</p>${preview}${mediaLine}</section>`;
}

function wire() {
  const save = el('or-save');
  if (save) save.addEventListener('click', () => {
    window.__downloadFile(current.result.blob, current.result.filename);
    window.__ae && window.__ae('feature_use', { slug: 'office-repair', feature: 'saved_document' });
  });

  const sr = el('or-save-renamed');
  if (sr) sr.addEventListener('click', () => {
    window.__downloadFile(current.renamed.blob, current.renamed.name);
    window.__ae && window.__ae('feature_use', { slug: 'office-repair', feature: 'saved_renamed' });
  });

  const st = el('or-save-text');
  if (st) st.addEventListener('click', () => {
    const base = (current.file.name || 'document').replace(/\.[^.]+$/, '');
    window.__downloadFile(new Blob([current.text], { type: 'text/plain;charset=utf-8' }), `${base}.txt`);
    window.__ae && window.__ae('feature_use', { slug: 'office-repair', feature: 'saved_text', route: current.route && current.route.route });
  });

  const sm = el('or-save-media');
  if (sm) sm.addEventListener('click', async () => {
    const { buildZip } = await import('../archive/archive-repair-core.js');
    const bytes = await buildZip(current.media.map((m) => ({ path: m.path, bytes: m.bytes })), {});
    const base = (current.file.name || 'document').replace(/\.[^.]+$/, '');
    window.__downloadFile(new Blob([bytes], { type: 'application/zip' }), `${base}-media.zip`);
    window.__ae && window.__ae('feature_use', { slug: 'office-repair', feature: 'saved_media' });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  const zone = el('orDrop');
  if (!zone) return;
  zone.addEventListener('tool-uploader:file', (e) => {
    const f = e.detail && (e.detail.file || (e.detail.files && e.detail.files[0]));
    if (f) analyse(f);
  });
  // A file the zone still turns away (not an office name at all): what its bytes are.
  zone.addEventListener('tool-uploader:reject', (e) => {
    const d = e.detail || {};
    if (d.reason === 'wrong_format' && d.file) reportAnatomy(d.file, 'wrong_format');
  });
  // The uploader's × removed the file; the report below describes it, so it goes too.
  zone.addEventListener('tool-uploader:clear', () => {
    if (door) { try { door.destroy(); } catch (_) { /* gone */ } door = null; }
    const out = el('or-out');
    if (out) { out.innerHTML = ''; out.hidden = true; }
  });
});

export { analyse };
